import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
  Divider,
  useTheme,
} from "@mui/material";
import TimelineIcon from "@mui/icons-material/Timeline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";

const BugJourneyTimelineDialog = ({
  bugJourney = [],
  createdAt,
  updatedAt,
}) => {
  const [open, setOpen] = React.useState(false);
  const theme = useTheme();

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Helper function to extract status information from journey item
  const extractStatusInfo = (item) => {
    if (!item) return null;

    if (
      typeof item === "object" &&
      item.status &&
      typeof item.status === "object"
    ) {
      return {
        index: item.index,
        id: item.status._id,
        status: item.status.status,
        statusCode: item.status.statusCode,
        displayText:
          item.status.status || item.status.statusCode || "Unknown Status",
      };
    }

    if (
      typeof item === "object" &&
      item.status &&
      typeof item.status === "string"
    ) {
      return {
        index: item.index || 0,
        id: item._id,
        status: item.status,
        statusCode: item.statusCode,
        displayText: item.status || item.statusCode || "Unknown Status",
      };
    }

    if (typeof item === "string") {
      return {
        index: 0,
        id: item,
        status: item,
        statusCode: null,
        displayText: item,
      };
    }

    return null;
  };

  const validBugJourney = React.useMemo(() => {
    if (!bugJourney || !Array.isArray(bugJourney)) return [];

    return bugJourney
      .map(extractStatusInfo)
      .filter((statusInfo) => statusInfo !== null)
      .sort((a, b) => a.index - b.index); // Sort by index to ensure correct order
  }, [bugJourney]);

  const validCreatedAt = React.useMemo(() => {
    return createdAt && moment(createdAt).isValid()
      ? moment(createdAt).toDate()
      : new Date();
  }, [createdAt]);

  const validUpdatedAt = React.useMemo(() => {
    return updatedAt && moment(updatedAt).isValid()
      ? moment(updatedAt).toDate()
      : new Date();
  }, [updatedAt]);

  const journeyWithTimestamps = React.useMemo(() => {
    if (validBugJourney.length === 0) return [];

    const result = [
      {
        statusInfo: validBugJourney[0],
        timestamp: validCreatedAt,
        isFirst: true,
      },
    ];

    // For subsequent statuses, use updated timestamp for the last one, and interpolate for others
    if (validBugJourney.length > 1) {
      const totalDuration = moment(validUpdatedAt).diff(moment(validCreatedAt));
      const timePerStatus = totalDuration / (validBugJourney.length - 1);

      for (let i = 1; i < validBugJourney.length; i++) {
        let timestamp;

        if (i === validBugJourney.length - 1) {
          timestamp = validUpdatedAt; // Last status gets the updated timestamp
        } else {
          // Interpolate timestamps for intermediate statuses
          timestamp = moment(validCreatedAt)
            .add(timePerStatus * i)
            .toDate();
        }

        result.push({
          statusInfo: validBugJourney[i],
          timestamp,
          isLast: i === validBugJourney.length - 1,
        });
      }
    }

    return result;
  }, [validBugJourney, validCreatedAt, validUpdatedAt]);

  // Check if we have any valid journey data
  if (validBugJourney.length === 0) {
    return null;
  }

  return (
    <>
      <Tooltip title="View Task Journey">
        <IconButton
          onClick={handleOpen}
          size="small"
          sx={{
            color: "primary.main",
            "&:hover": { backgroundColor: "rgba(25, 118, 210, 0.04)" },
          }}
        >
          <TimelineIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Task Journey History</Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ position: "relative", py: 2 }}>
            {/* Vertical timeline line */}
            <Box
              sx={{
                position: "absolute",
                left: 12,
                top: 12,
                bottom: 12,
                width: 2,
                bgcolor: theme.palette.divider,
                zIndex: 0,
              }}
            />

            {journeyWithTimestamps.map((item: any, arrayIndex) => (
              <Box
                key={`${item.statusInfo.id || item.statusInfo.status}-${
                  item.statusInfo.index
                }-${arrayIndex}`}
                sx={{
                  display: "flex",
                  mb: arrayIndex < journeyWithTimestamps.length - 1 ? 4 : 0,
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    zIndex: 1,
                    display: "flex",
                    justifyContent: "center",
                    width: 24,
                    height: 24,
                    mr: 2,
                  }}
                >
                  {item.isFirst || item.isLast ? (
                    <CheckCircleIcon
                      fontSize="small"
                      sx={{
                        fontSize: 24,
                        color: item.isLast
                          ? theme.palette.success.main
                          : theme.palette.primary.main,
                      }}
                    />
                  ) : (
                    <RadioButtonUncheckedIcon
                      fontSize="small"
                      sx={{
                        fontSize: 14,
                        mt: 0.5,
                        color: theme.palette.text.secondary,
                      }}
                    />
                  )}
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body1"
                        fontWeight={
                          item.isFirst || item.isLast ? "bold" : "normal"
                        }
                        sx={{
                          color: item.isLast
                            ? theme.palette.success.main
                            : item.isFirst
                            ? theme.palette.primary.main
                            : theme.palette.text.primary,
                        }}
                      >
                        {item.statusInfo.displayText}
                      </Typography>

                      {/* Show status code if different from status name */}
                      {item.statusInfo.statusCode &&
                        item.statusInfo.statusCode !==
                          item.statusInfo.status && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mt: 0.5 }}
                          >
                            Code: {item.statusInfo.statusCode}
                          </Typography>
                        )}

                      {/* Show journey step number */}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.5 }}
                      >
                        Step {item.statusInfo.index + 1} of{" "}
                        {validBugJourney.length}
                      </Typography>
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                      {moment(item.timestamp).format("MMM DD, YYYY HH:mm")}
                    </Typography>
                  </Box>

                  {item.isFirst && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      Task was created in this status
                    </Typography>
                  )}

                  {item.isLast && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      Current status of the task
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>

          {/* Show total journey summary */}
          {journeyWithTimestamps.length > 1 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="caption" color="text.secondary">
                  Total Journey Duration:{" "}
                  {moment
                    .duration(
                      moment(validUpdatedAt).diff(moment(validCreatedAt))
                    )
                    .humanize()}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block" }}
                >
                  {validBugJourney.length} status
                  {validBugJourney.length > 1 ? "es" : ""} tracked
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BugJourneyTimelineDialog;
